/**
 * API Error Handling Utilities for Delivery Options
 *
 * This module provides utilities for parsing and handling API error responses
 * from the delivery options endpoint. It handles the standardized error format
 * returned by the backend's AllExceptionsFilter.
 */

/**
 * Standardized API error response format from the backend
 * Used by AllExceptionsFilter
 */
export interface ApiErrorResponse {
  statusCode: number;
  errorMsg: string | string[];
  timestamp: string; // ISO 8601 format
  path: string;
}

/**
 * Type guard to check if an error has the expected Axios error structure
 */
function isAxiosError(
  error: unknown
): error is { response?: { data?: ApiErrorResponse } } {
  return typeof error === "object" && error !== null && "response" in error;
}

/**
 * Extracts a displayable error message from an API error response
 * Handles both string and array formats for errorMsg
 *
 * @param error - The caught error object (typically AxiosError)
 * @param fallbackMessage - Message to use if errorMsg is not available
 * @returns A string suitable for display to the user
 */
export function extractApiErrorMessage(
  error: unknown,
  fallbackMessage: string = "Something went wrong, please try again."
): string {
  // Check if error has the expected structure
  if (!isAxiosError(error) || !error.response?.data) {
    return fallbackMessage;
  }

  const { errorMsg } = error.response.data;

  // Handle missing or undefined errorMsg
  if (errorMsg === undefined || errorMsg === null) {
    return fallbackMessage;
  }

  // Handle string errorMsg
  if (typeof errorMsg === "string") {
    // Return fallback if empty string
    if (errorMsg.trim() === "") {
      return fallbackMessage;
    }
    return errorMsg;
  }

  // Handle array errorMsg
  if (Array.isArray(errorMsg)) {
    // Return fallback if empty array
    if (errorMsg.length === 0) {
      return fallbackMessage;
    }
    // Join array elements with newlines for point-by-point display
    return errorMsg.join("\n");
  }

  // Fallback for unexpected errorMsg type
  return fallbackMessage;
}

/**
 * Logs API error details to console for debugging
 * Includes statusCode, errorMsg, timestamp, and path when available
 *
 * @param error - The caught error object
 * @param context - Additional context string (e.g., "fetchDeliveryOptions")
 */
export function logApiError(error: unknown, context: string): void {
  console.error(`[${context}] API Error:`, {
    context,
    ...(isAxiosError(error) && error.response?.data
      ? {
          statusCode: error.response.data.statusCode,
          errorMsg: error.response.data.errorMsg,
          timestamp: error.response.data.timestamp,
          path: error.response.data.path,
        }
      : { error }),
  });
}
